/**
 * canvasState.js — CANVAS-SHELL-1 (01/10/2026): estado do VUKZ Canvas.
 *
 * Fonte única do contrato de estado do Canvas e da sua persistência.
 *
 * REGRA (decisão 1, revisão 01/10): o que persiste é um DESCRIPTOR, nunca
 * dados. Wellness é sensível e fica stale; o Draft C3 é source of truth no
 * servidor. Ao restaurar: descriptor → componente monta → autorização
 * normal → refetch → Canvas atual. Nada de valores de wellness nem cópia
 * de Draft em sessionStorage — `sanitizeDescriptor` descarta por
 * construção qualquer chave fora da whitelist.
 *
 * Contrato mínimo:
 *   canvasState = { isOpen, kind, subject, params, selection, version }
 *   kind     ∈ CANVAS_KINDS ('wellness' | 'training_draft' | 'training_created') | null
 *   subject  = { type: 'athlete', athleteId } | { type: 'draft', draftId, draftVersion }
 *            | { type: 'plan', planId } | null
 *   params   = { period? }                          (enum fechado)
 *   selection= { metric? | blockLocalId? }          (enum fechado / string estável)
 *
 * Referências usam IDs ESTÁVEIS do Form Contract/Draft (block_local_id),
 * nunca índice numérico de bloco (decisão 4).
 *
 * Nenhuma lógica de wellness ou training vive aqui — só forma e persistência.
 */

export const CANVAS_STATE_VERSION = 1;
export const CANVAS_STORAGE_KEY = 'vukz_canvas_descriptor_v1';

export const CANVAS_KINDS = Object.freeze(['wellness', 'training_draft', 'training_created', 'training_edit']);

// Enums fechados — espelhados no backend (wellness_context.PERIODS e, quando a
// seleção viajar via UI_REFERENCE_CONTEXT, ui_reference.py). Nada de texto livre.
export const CANVAS_PERIODS = Object.freeze(['7d', '14d', '30d']);
export const WELLNESS_METRICS = Object.freeze([
  'sleep_hours', 'sleep_quality', 'fatigue', 'mood', 'soreness', 'injury', 'coverage',
]);

const MAX_ID_LEN = 64;
const STABLE_ID_RE = /^[A-Za-z0-9_\-:.]{1,64}$/;

export const EMPTY_CANVAS_STATE = Object.freeze({
  isOpen: false,
  kind: null,
  subject: null,
  params: {},
  selection: null,
  version: CANVAS_STATE_VERSION,
});

/* ── helpers puros ─────────────────────────────────────────────────── */

function posInt(v) {
  const n = typeof v === 'string' ? parseInt(v, 10) : v;
  return Number.isInteger(n) && n > 0 ? n : null;
}

function stableId(v) {
  return typeof v === 'string' && v.length <= MAX_ID_LEN && STABLE_ID_RE.test(v) ? v : null;
}

function sanitizeSubject(kind, subject) {
  if (!subject || typeof subject !== 'object') return null;
  if (kind === 'wellness') {
    const athleteId = posInt(subject.athleteId);
    return athleteId ? { type: 'athlete', athleteId } : null;
  }
  if (kind === 'training_draft') {
    const draftId = stableId(subject.draftId);
    if (!draftId) return null;
    const out = { type: 'draft', draftId };
    const dv = posInt(subject.draftVersion);
    if (dv) out.draftVersion = dv;
    const athleteId = posInt(subject.athleteId);
    if (athleteId) out.athleteId = athleteId;
    return out;
  }
  if (kind === 'training_created') {
    // CANVAS-COMPLETE-1: o plano canônico é o sujeito — plan_id muda →
    // conteúdo novo. Nenhum dado do plano persiste (refetch via GET draft).
    const planId = posInt(subject.planId);
    return planId ? { type: 'plan', planId } : null;
  }
  if (kind === 'training_edit') {
    // EDIT-CANVAS-1: sujeito = plano canônico sendo editado + revisão FONTE
    // (nunca a atual: o backend deriva stale). draftVersion → refetch.
    const planId = posInt(subject.planId);
    if (!planId) return null;
    const out = { type: 'plan_edit', planId };
    const sr = posInt(subject.sourceRevision);
    if (sr) out.sourceRevision = sr;
    const dv = posInt(subject.draftVersion);
    if (dv) out.draftVersion = dv;
    return out;
  }
  return null;
}

function sanitizeParams(params) {
  if (!params || typeof params !== 'object') return {};
  const out = {};
  if (CANVAS_PERIODS.includes(params.period)) out.period = params.period;
  return out;
}

function sanitizeSelection(kind, selection) {
  if (!selection || typeof selection !== 'object') return null;
  if (kind === 'wellness') {
    return WELLNESS_METRICS.includes(selection.metric) ? { metric: selection.metric } : null;
  }
  if (kind === 'training_draft') {
    const blockLocalId = stableId(selection.blockLocalId);
    return blockLocalId ? { blockLocalId } : null;
  }
  return null;
}

/**
 * Normaliza um descriptor arbitrário para o contrato. Descarta tudo que não
 * está na whitelist (por construção, nenhum dado vaza para a persistência).
 * Retorna null se `kind` for inválido ou o subject obrigatório faltar.
 */
export function sanitizeDescriptor(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const kind = CANVAS_KINDS.includes(raw.kind) ? raw.kind : null;
  if (!kind) return null;
  const subject = sanitizeSubject(kind, raw.subject);
  if (!subject) return null;
  return {
    kind,
    subject,
    params: sanitizeParams(raw.params),
    selection: sanitizeSelection(kind, raw.selection),
  };
}

/** Descriptor (sem isOpen/version) a partir de um estado completo. */
export function toDescriptor(state) {
  if (!state || !state.kind) return null;
  return sanitizeDescriptor(state);
}

/**
 * CANVAS-ENGINE-1: `data.workspace` (backend) → descriptor do Canvas.
 * O backend é a autoridade sobre QUAL workspace está ativo; aqui só
 * traduzimos snake_case → contrato local (athlete_id → athleteId) e
 * descartamos tudo que não é descriptor (completion/fields/blocks ficam
 * em `workspaceLive`, nunca persistidos). Draft é 1 por coach → draftId
 * estável 'CREATE_TRAINING'; draftVersion = revision (muda → refetch).
 */
export const TRAINING_DRAFT_ID = 'CREATE_TRAINING';

export function descriptorFromWorkspace(ws) {
  if (!ws || typeof ws !== 'object' || !ws.kind) return null;
  const subj = ws.subject || {};
  if (ws.kind === 'wellness') {
    return sanitizeDescriptor({
      kind: 'wellness',
      subject: { type: 'athlete', athleteId: subj.athlete_id ?? subj.athleteId },
      params: ws.params || {},
    });
  }
  if (ws.kind === 'training_draft') {
    return sanitizeDescriptor({
      kind: 'training_draft',
      subject: {
        type: 'draft',
        draftId: TRAINING_DRAFT_ID,
        draftVersion: ws.revision ?? ws.draft?.revision,
        athleteId: subj.athlete_id ?? subj.athleteId,
      },
    });
  }
  if (ws.kind === 'training_created') {
    return sanitizeDescriptor({
      kind: 'training_created',
      subject: { type: 'plan', planId: ws.plan?.id ?? ws.plan_id },
    });
  }
  if (ws.kind === 'training_edit') {
    return sanitizeDescriptor({
      kind: 'training_edit',
      subject: {
        type: 'plan_edit',
        planId: ws.source?.plan_id,
        sourceRevision: ws.source?.revision,
        draftVersion: ws.revision ?? ws.draft?.revision,
      },
    });
  }
  return null;
}

/** Dois descriptors apontam para o mesmo conteúdo? (kind + subject + params) */
export function sameDescriptor(a, b) {
  if (!a || !b) return a === b;
  return a.kind === b.kind
    && JSON.stringify(a.subject || null) === JSON.stringify(b.subject || null)
    && JSON.stringify(a.params || {}) === JSON.stringify(b.params || {});
}

/* ── persistência (sessionStorage; só descriptor) ──────────────────── */

function storage() {
  try { return window.sessionStorage; } catch { return null; }
}

export function loadCanvasDescriptor() {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(CANVAS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CANVAS_STATE_VERSION) return null;
    return sanitizeDescriptor(parsed);
  } catch {
    return null;
  }
}

export function saveCanvasDescriptor(state) {
  const s = storage();
  if (!s) return;
  try {
    const d = toDescriptor(state);
    if (!d) { s.removeItem(CANVAS_STORAGE_KEY); return; }
    s.setItem(CANVAS_STORAGE_KEY, JSON.stringify({ ...d, version: CANVAS_STATE_VERSION }));
  } catch { /* noop */ }
}

export function clearCanvasDescriptor() {
  const s = storage();
  try { s && s.removeItem(CANVAS_STORAGE_KEY); } catch { /* noop */ }
}

/** Estado inicial: descriptor restaurado (se houver) — fechado por default. */
export function initialCanvasState() {
  const d = loadCanvasDescriptor();
  return d ? { ...EMPTY_CANVAS_STATE, ...d, isOpen: false } : { ...EMPTY_CANVAS_STATE };
}

/* ── reducer puro ──────────────────────────────────────────────────── */

export function canvasReducer(state, action) {
  switch (action.type) {
    case 'open': {
      const d = sanitizeDescriptor(action.descriptor);
      if (!d) return state;
      return { ...EMPTY_CANVAS_STATE, ...d, isOpen: true };
    }
    case 'update': {
      if (!state.kind) return state;
      const merged = sanitizeDescriptor({
        kind: state.kind,
        subject: { ...state.subject, ...(action.patch?.subject || {}) },
        params: { ...state.params, ...(action.patch?.params || {}) },
        selection: action.patch?.selection === undefined ? state.selection : action.patch.selection,
      });
      return merged ? { ...state, ...merged } : state;
    }
    case 'select': {
      if (!state.kind) return state;
      return { ...state, selection: sanitizeSelection(state.kind, action.selection) };
    }
    case 'clearSelection':
      return state.selection === null ? state : { ...state, selection: null };
    case 'close':
      return state.isOpen ? { ...state, isOpen: false } : state;
    case 'show':
      return state.isOpen ? state : { ...state, isOpen: true };
    case 'reset':
      return { ...EMPTY_CANVAS_STATE };
    default:
      return state;
  }
}
